/**
 * Loading Spinner Component
 * 
 * A reusable loading spinner component with customizable size and styling.
 * Used throughout the application to indicate loading states during
 * data fetching, form submissions, and route transitions.
 */

import React from 'react';

/**
 * Props interface for LoadingSpinner component
 */
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg'; // Size variant for the spinner
  className?: string;        // Additional CSS classes
}

/**
 * LoadingSpinner Component
 * 
 * Renders a centered loading spinner with customizable size.
 * Displays a full-screen loading state with spinner animation
 * and loading text.
 * 
 * @param size - Size of the spinner (sm, md, lg)
 * @param className - Additional CSS classes for customization
 * @returns JSX element containing the loading spinner
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  className = '' 
}) => {
  // Define size classes for different spinner sizes
  const sizeClasses = {
    sm: 'h-4 w-4',   // Small: 16x16px
    md: 'h-8 w-8',   // Medium: 32x32px (default)
    lg: 'h-12 w-12'  // Large: 48x48px
  };

  return (
    <div className="text-center">
      {/* Animated spinning circle */}
      <div 
        className={`animate-spin rounded-full border-b-2 border-primary-600 mx-auto mb-4 ${sizeClasses[size]} ${className}`}
      />
      {/* Loading text */}
      <p className="text-gray-600">Loading...</p>
    </div>
  );
};
